import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, ShoppingCart, Plus, AlertCircle, Calendar as CalendarIcon, CalendarCheck, Sparkles, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { clientPortalService } from '../../services/clientPortal.service';
import { toast } from 'sonner';
import { useCart } from '../../contexts/CartContext';
import { SuggestedQtyChip } from '../../components/insights/SuggestedQtyChip';
import { CapChip } from '../../components/insights/CapChip';
import { buildCapMap } from '../../lib/overOrder';

// Format a Date to YYYY-MM-DD using LOCAL parts (never toISOString — that shifts
// the calendar day east of UTC). Mirrors the backend's date-only contract.
const toDateOnly = (d) => {
  if (!d) return null;
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${day}`;
};

// Parse YYYY-MM-DD to a local-noon Date (timezone-safe).
const parseDateOnly = (s) => (s ? new Date(`${s}T12:00:00`) : null);

const Shop = () => {
  const [calendar, setCalendar] = useState(null);
  const [loadingCal, setLoadingCal] = useState(true);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [suggestions, setSuggestions] = useState({});
  const [caps, setCaps] = useState({});

  const {
    cart,
    selectedDeliveryDate,
    setSelectedDeliveryDate,
    setAvailableProductIds,
    revalidateCart,
    addToCart,
    setItemQuantity,
    getCartItemCount,
    openCart,
  } = useCart();

  const cartQtyById = useMemo(() => {
    const map = {};
    (cart || []).forEach((it) => { map[it.id] = it.quantity; });
    return map;
  }, [cart]);

  // Lookups derived from the delivery calendar. Dates the server flagged with
  // `pickable: false` (e.g. `beyond_advance_window`) are excluded here so the
  // Calendar automatically renders them disabled.
  const pickableSet = useMemo(
    () => new Set((calendar?.dates || [])
      .filter((d) => d.pickable !== false)
      .map((d) => d.date)),
    [calendar]
  );
  const beyondAdvanceSet = useMemo(
    () => new Set((calendar?.dates || [])
      .filter((d) => d.disabled_reason === 'beyond_advance_window')
      .map((d) => d.date)),
    [calendar]
  );
  const menuByDate = useMemo(() => {
    const map = {};
    (calendar?.dates || []).forEach((d) => { if (d.has_menu) map[d.date] = d; });
    return map;
  }, [calendar]);
  const menuDates = useMemo(
    () => Object.keys(menuByDate).map(parseDateOnly),
    [menuByDate]
  );

  const horizonDate = useMemo(() => {
    const weeks = calendar?.weeks || 8;
    const d = new Date();
    d.setDate(d.getDate() + weeks * 7);
    return d;
  }, [calendar]);

  useEffect(() => {
    loadCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCalendar = async () => {
    try {
      const data = await clientPortalService.getDeliveryCalendar(8);
      setCalendar(data);
      // If a date was previously selected (e.g. returning from checkout) and is
      // still pickable, restore it and load its products.
      const set = new Set((data.dates || []).map((d) => d.date));
      const prev = selectedDeliveryDate ? toDateOnly(selectedDeliveryDate) : null;
      if (prev && set.has(prev)) {
        await loadProductsForDate(selectedDeliveryDate, { silent: true });
      }
    } catch (error) {
      console.error('Error loading delivery calendar:', error);
      toast.error('Failed to load delivery calendar');
    } finally {
      setLoadingCal(false);
    }
  };

  const loadProductsForDate = async (date, { silent = false } = {}) => {
    const dateStr = toDateOnly(date);
    if (!dateStr) return;
    setLoadingProducts(true);
    try {
      const data = await clientPortalService.getProducts({ delivery_date: dateStr });
      setProducts(data);
      const ids = data.map((p) => p.id);
      setAvailableProductIds(ids);
      // Suggestions are best-effort — fetched in parallel, never blocking product render.
      loadInsights(dateStr);
      // Revalidate cart against the new date — auto-remove unavailable items.
      const removed = revalidateCart(ids);
      if (removed.length > 0 && !silent) {
        toast.warning(
          `Removed ${removed.length} item(s) not available on this date: ${removed.map((r) => r.name).join(', ')}`
        );
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products for this date');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSelectDate = (date) => {
    if (!date) return;
    const dateStr = toDateOnly(date);
    if (!pickableSet.has(dateStr)) return;
    setSelectedDeliveryDate(date);
    loadProductsForDate(date);
  };

  const loadInsights = async (dateStr) => {
    setSuggestions({});
    setCaps({});
    try {
      const res = await clientPortalService.getOrderInsights(dateStr);
      const map = {};
      (res.suggestions || []).forEach((s) => { map[s.product_id] = s; });
      setSuggestions(map);
      // The ceiling on each product, so a line can be warned about as it is
      // typed rather than refused at checkout. Switchable off by the bakery.
      setCaps(res.warnings_enabled === false ? {} : buildCapMap(res));
    } catch {
      // Insights are optional; a failure must never break the shop.
    }
  };

  const handleApplySuggestion = (product, qty) => {
    addToCart(product);
    setItemQuantity(product.id, qty);
    toast.success(`Added ${qty} × ${product.name} (suggested)`);
  };

  const selectedDateStr = selectedDeliveryDate ? toDateOnly(selectedDeliveryDate) : null;
  const selectedMenu = selectedDateStr ? menuByDate[selectedDateStr] : null;
  const isExclusive = selectedMenu?.mode === 'exclusive';

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(products.map((p) => p.category))];

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`Added ${product.name} to cart`);
  };

  const getAvailabilityBadge = (availability) => {
    const styles = {
      available: 'bg-green-100 text-green-800',
      limited: 'bg-yellow-100 text-yellow-800',
      'pre-order': 'bg-blue-100 text-blue-800',
      'out-of-stock': 'bg-red-100 text-red-800',
    };
    return styles[availability] || 'bg-gray-100 text-gray-800';
  };

  const prettySelected = selectedDeliveryDate
    ? selectedDeliveryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="space-y-6" data-testid="shop-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shop</h1>
          <p className="text-sm text-gray-600 mt-1">Pick a delivery date, then build your order</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 relative" onClick={openCart} data-testid="cart-btn">
          <ShoppingCart className="h-4 w-4 mr-2" />
          View Cart
          {getCartItemCount() > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {getCartItemCount()}
            </span>
          )}
        </Button>
      </div>

      {/* Step 1: Date picker */}
      <Card data-testid="date-picker-card">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-auto">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-500 text-white text-xs font-bold">1</span>
                <h2 className="text-base font-semibold text-gray-900">Choose a delivery date</h2>
              </div>
              {loadingCal ? (
                <div className="flex items-center justify-center h-64 w-72" data-testid="calendar-loading">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
                </div>
              ) : (
                <Calendar
                  mode="single"
                  selected={selectedDeliveryDate || undefined}
                  onSelect={handleSelectDate}
                  disabled={(date) => !pickableSet.has(toDateOnly(date))}
                  modifiers={{ menu: menuDates }}
                  modifiersClassNames={{ menu: 'menu-day-dot' }}
                  fromDate={new Date()}
                  toDate={horizonDate}
                  className="rounded-lg border"
                  data-testid="delivery-calendar"
                />
              )}
              <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                Special menu available — book ahead
              </div>
              {beyondAdvanceSet.size > 0 && calendar?.advance_horizon_boundary && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 max-w-xs"
                  data-testid="advance-window-notice"
                  title="Ordering for this date opens closer to the delivery day."
                >
                  <CalendarIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <span>
                    Ordering for dates beyond{' '}
                    <strong>{new Date(`${calendar.advance_horizon_boundary}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</strong>
                    {' '}opens closer to the delivery day.
                  </span>
                </div>
              )}
            </div>

            {/* Selected-date summary */}
            <div className="flex-1 flex items-center">
              {selectedDeliveryDate ? (
                <div className="w-full" data-testid="selected-date-summary">
                  <div className="flex items-center gap-2 text-green-700">
                    <CalendarCheck className="h-5 w-5" />
                    <span className="font-semibold">Delivering {prettySelected}</span>
                  </div>
                  {selectedMenu?.is_override && (
                    <Badge className="mt-2 bg-blue-100 text-blue-800">Special delivery day</Badge>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    {isExclusive
                      ? 'This is a curated menu — only the items below can be ordered for this date.'
                      : 'Add items below. Your cart is scoped to this delivery date.'}
                  </p>
                </div>
              ) : (
                <div className="w-full text-center py-8" data-testid="no-date-selected">
                  <CalendarIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Select an available delivery date to see what you can order.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exclusive menu banner */}
      {selectedDeliveryDate && isExclusive && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 flex items-start gap-3" data-testid="exclusive-menu-banner">
          <Sparkles className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-purple-900">
              {selectedMenu.menu_title || 'Special Menu'}
            </p>
            <p className="text-xs text-purple-800/80">
              Only these items are available for {prettySelected}.
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Products (only after a date is chosen) */}
      {!selectedDeliveryDate ? null : (
        <>
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-500 text-white text-xs font-bold">2</span>
            <h2 className="text-base font-semibold text-gray-900">Add products</h2>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loadingProducts ? (
            <div className="flex items-center justify-center min-h-[300px]" data-testid="shop-loading">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
                <p className="mt-4 text-gray-600">Loading products...</p>
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12" data-testid="no-products">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No products available for this date</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
              {filteredProducts.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`product-${product.id}`}>
                  <div className="relative aspect-square">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    {product.is_seasonal && (
                      <Badge className="absolute top-2 left-2 bg-amber-500">Seasonal</Badge>
                    )}
                    <Badge className={`absolute top-2 right-2 ${getAvailabilityBadge(product.availability)}`}>
                      {product.availability}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <div className="mb-2">
                      <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                      <p className="text-xs text-gray-500 uppercase">{product.category}</p>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3 h-10">{product.description}</p>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        {product.has_discount ? (
                          <>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-gray-400 line-through">${product.wholesale_price.toFixed(2)}</p>
                              <Badge className="bg-green-100 text-green-800 text-xs">{product.discount_percent}% off</Badge>
                            </div>
                            <p className="text-lg font-bold text-green-600">${product.your_price.toFixed(2)}</p>
                            <p className="text-xs text-green-600">You save ${product.discount_amount.toFixed(2)}</p>
                          </>
                        ) : (
                          <p className="text-lg font-bold text-amber-600">${product.wholesale_price.toFixed(2)}</p>
                        )}
                        <p className="text-xs text-gray-500">{product.pack_size} ({product.pack_quantity}pc)</p>
                      </div>
                    </div>
                    {product.allergens && product.allergens.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-red-600">Allergens: {product.allergens.join(', ')}</p>
                      </div>
                    )}
                    {(suggestions[product.id] || caps[product.id]) && (
                      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid={`insights-${product.id}`}>
                        {suggestions[product.id] && (
                          <span data-testid={`suggestion-${product.id}`}>
                            <SuggestedQtyChip
                              suggestion={suggestions[product.id]}
                              onApply={(qty) => handleApplySuggestion(product, qty)}
                            />
                          </span>
                        )}
                        {caps[product.id] && (
                          <CapChip
                            cap={caps[product.id]}
                            orderedQty={cartQtyById[product.id] || 0}
                            onUseCap={(qty) => setItemQuantity(product.id, qty)}
                          />
                        )}
                      </div>
                    )}
                    <Button
                      className="w-full bg-amber-500 hover:bg-amber-600"
                      onClick={() => handleAddToCart(product)}
                      disabled={product.availability === 'out-of-stock'}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {getCartItemCount() > 0 && (
            <div className="sticky bottom-4 flex justify-end">
              <Button className="bg-amber-500 hover:bg-amber-600 shadow-lg" onClick={openCart} data-testid="review-cart-btn">
                Review Cart ({getCartItemCount()}) <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Shop;
